import {useNavigation, useRoute, RouteProp, useFocusEffect} from '@react-navigation/native';
import {useEffect, useLayoutEffect, useState, useCallback} from 'react';
import {Platform, StatusBar, View, DeviceEventEmitter} from 'react-native';
import HomeScreenTabs from '~components/HomeScreenComponent';
import MyEventTabs from '~components/HomeScreenComponent/MyEventTabs';
import useTabBar from '~containers/Core/navigation/AppScreens/zustand';
import {useOnboardingStore} from '~providers/onboarding/zustand';
import {logScreenView} from '~Utils/firebaseAnalytics';
import {useTheme} from '~contexts/ThemeContext';
import UpcomingEventCard from '~components/UpcomingEventCard';
import { useUpcomingEvents } from '~hooks/event/useUpcomingEvents';
import useGetCurrentPosition from '~components/LocationModal/hooks/useGetCurrentPosition';
import { useMapsContext } from '~providers/maps/zustand';
import { useGetActivePlan } from '~hooks/subscription/useGetActivePlan';
import { useSubscriptionContext } from '~contexts/SubscriptionContext';

// Define type for route params
type HomeRouteParams = {
  myEvent?: boolean;
};

const HomeScreen = () => {
  const {colors} = useTheme();
  const {setIsTabBarDisabled} = useTabBar();
  const {reset} = useOnboardingStore();
  const navigation = useNavigation();
  const routes = useRoute<RouteProp<Record<string, HomeRouteParams>, string>>();
  const [isMyEvent, setMyEvent] = useState<boolean>();
  const { upcomingEvent, isLoading } = useUpcomingEvents();
  const [showUpcomingCard, setShowUpcomingCard] = useState(true);
  const { getCurrentPosition } = useGetCurrentPosition();
  const { currentPositionState } = useMapsContext();
  const [lastEmittedLocation, setLastEmittedLocation] = useState<{lat: number; long: number; country: string | undefined} | null>(null);
  
  // Subscription context and API
  const { data: activePlan, isLoading: isActivePlanLoading } = useGetActivePlan();
  const { setActivePlan, setIsLoading: setSubscriptionLoading } = useSubscriptionContext();

  useEffect(() => {
    if (routes.params && 'myEvent' in routes.params) {
      setMyEvent(routes.params.myEvent);
    }
  }, [routes.params]);

  // Fetch and save active plan to context
  useEffect(() => {
    if (activePlan) {
      setActivePlan(activePlan);
    }
    setSubscriptionLoading(isActivePlanLoading);
  }, [activePlan, isActivePlanLoading, setActivePlan, setSubscriptionLoading]);

  useLayoutEffect(() => {
    reset();
  }, []);

  // Fetch current location when landing on home screen (for web)
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        getCurrentPosition(false);
      }
    }, [getCurrentPosition])
  );

  // Emit homeLocationChange event ONLY when location actually changes
  useEffect(() => {
    if (currentPositionState && Platform.OS === 'web') {
      const newLocation = {
        lat: currentPositionState.latitude,
        long: currentPositionState.longitude,
        country: currentPositionState.country,
      };

      // Only emit if location has actually changed
      if (!lastEmittedLocation || 
          lastEmittedLocation.lat !== newLocation.lat ||
          lastEmittedLocation.long !== newLocation.long ||
          lastEmittedLocation.country !== newLocation.country) {
        
        DeviceEventEmitter.emit('homeLocationChange', newLocation);
        setLastEmittedLocation(newLocation);
      }
    }
  }, [currentPositionState]);

  useEffect(() => {
    logScreenView('Home', 'Home');
    // Ensure tab bar is visible on the Home screen
    setIsTabBarDisabled(false);
  }, [setIsTabBarDisabled]);

  const handleDismissCard = () => {
    setShowUpcomingCard(false);
  };

  // Make sure tab bar is visible when rendering home screen
  useLayoutEffect(() => {
    setIsTabBarDisabled(false);
  }, [setIsTabBarDisabled]);

  return (
    <>
      <View style={{flex: 1, backgroundColor: colors.white}}>
        {isMyEvent ? <MyEventTabs /> : <HomeScreenTabs />}
        
        {/* Positioned at the bottom of the screen, above tab bar */}
        {upcomingEvent && showUpcomingCard && (
          <View style={{
            position: 'absolute', 
            bottom: Platform.OS === 'ios' ? 84 : 60, // Position above the new tab bar
            left: 0,
            right: 0,
            alignItems: 'center',
            paddingHorizontal: 20,
            zIndex: 999, // Ensure it's above other elements
          }}>
            <UpcomingEventCard event={upcomingEvent} onDismiss={handleDismissCard} />
          </View>
        )}
      </View>
    </>
  );
};

export default HomeScreen;
